import React from 'react'
import { Button, Heading, Input } from '../ui'
import type { AdmContract } from '../../types/admDespesas'

export type ContractEditModalState = {
  id: string
  contractNumber: string
  counterpartyName: string
  counterpartyDocument: string
  startDate: string
  endDate: string
  totalValue: string
  notes: string
  objective: string
  signatureDate: string
  paymentNotes: string
  paymentDate: string
  manualNotes: string
  contractorName: string
  contractorDocument: string
  importMethod: string
}

export function contractEditStateFromRecord(c: AdmContract): ContractEditModalState {
  return {
    id: c.id,
    contractNumber: c.contract_number ?? '',
    counterpartyName: c.counterparty_name ?? '',
    counterpartyDocument: c.counterparty_document ?? '',
    startDate: c.start_date ? c.start_date.slice(0, 10) : '',
    endDate: c.end_date ? c.end_date.slice(0, 10) : '',
    totalValue: c.total_value != null ? String(c.total_value) : '',
    notes: c.notes ?? '',
    objective: c.objective ?? '',
    signatureDate: c.signature_date ? c.signature_date.slice(0, 10) : '',
    paymentNotes: c.payment_notes ?? '',
    paymentDate: c.payment_date ? c.payment_date.slice(0, 10) : '',
    manualNotes: c.manual_notes ?? '',
    contractorName: c.contractor_name ?? '',
    contractorDocument: c.contractor_document ?? '',
    importMethod: c.import_method ?? '',
  }
}

export function contractEditPatchFromModal(modal: ContractEditModalState) {
  return {
    contract_number: modal.contractNumber.trim() || null,
    counterparty_name: modal.counterpartyName.trim() || null,
    counterparty_document: modal.counterpartyDocument.trim() || null,
    start_date: modal.startDate.trim() || null,
    end_date: modal.endDate.trim() || null,
    total_value: modal.totalValue.trim() ? Number(modal.totalValue) : null,
    notes: modal.notes.trim() || null,
    objective: modal.objective.trim() || null,
    signature_date: modal.signatureDate.trim() || null,
    payment_notes: modal.paymentNotes.trim() || null,
    payment_date: modal.paymentDate.trim() || null,
    manual_notes: modal.manualNotes.trim() || null,
    contractor_name: modal.contractorName.trim() || null,
    contractor_document: modal.contractorDocument.trim() || null,
    import_method: modal.importMethod.trim() || null,
  }
}

type AdmDespesasContractEditModalProps = {
  modal: ContractEditModalState
  submitting: boolean
  onChange: (updater: (prev: ContractEditModalState) => ContractEditModalState) => void
  onClose: () => void
  onSave: () => void
}

const AdmDespesasContractEditModal: React.FC<AdmDespesasContractEditModalProps> = ({
  modal,
  submitting,
  onChange,
  onClose,
  onSave,
}) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
        Editar contrato
      </Heading>
      <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Número do contrato</label>
          <Input
            value={modal.contractNumber}
            onChange={(e) => onChange((p) => ({ ...p, contractNumber: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Contraparte</label>
          <Input
            value={modal.counterpartyName}
            onChange={(e) => onChange((p) => ({ ...p, counterpartyName: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Documento (CPF/CNPJ)</label>
          <Input
            value={modal.counterpartyDocument}
            onChange={(e) => onChange((p) => ({ ...p, counterpartyDocument: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data início</label>
          <Input
            type="date"
            value={modal.startDate}
            onChange={(e) => onChange((p) => ({ ...p, startDate: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data fim</label>
          <Input
            type="date"
            value={modal.endDate}
            onChange={(e) => onChange((p) => ({ ...p, endDate: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Valor total</label>
          <Input
            type="number"
            step="any"
            value={modal.totalValue}
            onChange={(e) => onChange((p) => ({ ...p, totalValue: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações</label>
          <textarea
            rows={3}
            value={modal.notes}
            onChange={(e) => onChange((p) => ({ ...p, notes: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Objetivo</label>
          <textarea
            rows={2}
            value={modal.objective}
            onChange={(e) => onChange((p) => ({ ...p, objective: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data de assinatura</label>
          <Input
            type="date"
            value={modal.signatureDate}
            onChange={(e) => onChange((p) => ({ ...p, signatureDate: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2 md:col-start-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Método de importação</label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Texto livre, no mesmo estilo do meio de pagamento da NF-e (ex.: upload manual, N8N, planilha).</p>
          <Input
            value={modal.importMethod}
            onChange={(e) => onChange((p) => ({ ...p, importMethod: e.target.value }))}
            placeholder="ex.: upload manual, N8N"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Contratante</label>
          <Input
            value={modal.contractorName}
            onChange={(e) => onChange((p) => ({ ...p, contractorName: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Documento do contratante (CPF/CNPJ)</label>
          <Input
            value={modal.contractorDocument}
            onChange={(e) => onChange((p) => ({ ...p, contractorDocument: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações de pagamento</label>
          <textarea
            rows={2}
            value={modal.paymentNotes}
            onChange={(e) => onChange((p) => ({ ...p, paymentNotes: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Data pagamento</label>
          <Input
            type="date"
            value={modal.paymentDate}
            onChange={(e) => onChange((p) => ({ ...p, paymentDate: e.target.value }))}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Observações manuais</label>
          <textarea
            rows={3}
            value={modal.manualNotes}
            onChange={(e) => onChange((p) => ({ ...p, manualNotes: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="solid" onClick={onSave} disabled={submitting}>
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  </div>
)

export default AdmDespesasContractEditModal
